import React, { useEffect, useState, useCallback } from "react"
import { Box, Typography, Progress, IconButton, ThumbsUpIcon, ThumbsDownIcon } from "@carrotfertility/carotene-core"
import {
  Form,
  FormButton,
  FormRadioGroup,
  FormTextField,
  useWatch,
  useFormContext
} from "@carrotfertility/carotene-core-x"
import { useIntl, FormattedMessage } from "react-intl"
import {
  useCreateMemberContentFeedback,
  useMemberContentFeedback,
  CreateFeedbackRequest
} from "./hooks/useMemberContentFeedback"
import { reportError } from "#/utils/ErrorReporting"
import { FeedbackSelect } from "./FeedbackSelect"

export enum HelpfulRating {
  WAS_HELPFUL = "WAS_HELPFUL",
  WAS_NOT_HELPFUL = "WAS_NOT_HELPFUL"
}

interface FeedbackContainerProps {
  contentfulEntryId: string
  categoryEntryId: string
  contentType: string
}

interface FeedbackFormValues {
  helpfulRating: HelpfulRating | ""
  memberFeedbackSelections: string[]
  memberFeedbackText: string
}

interface FeedbackFormContentProps {
  hasExistingFeedback: boolean
  existingFeedbackDate?: string
  originalValues?: FeedbackFormValues
  isError: boolean
}

function FeedbackFormContent({
  hasExistingFeedback,
  existingFeedbackDate,
  originalValues,
  isError
}: FeedbackFormContentProps): JSX.Element {
  const intl = useIntl()
  const { setValue } = useFormContext<FeedbackFormValues>()
  const helpfulRating = useWatch({ name: "helpfulRating" })
  const memberFeedbackSelections = useWatch({ name: "memberFeedbackSelections" })
  const memberFeedbackText = useWatch({ name: "memberFeedbackText" })
  const [previousHelpfulRating, setPreviousHelpfulRating] = useState<HelpfulRating | "" | undefined>(helpfulRating)

  const hasChanges = React.useMemo(() => {
    if (!hasExistingFeedback || !originalValues) return true

    return (
      helpfulRating !== originalValues.helpfulRating ||
      memberFeedbackSelections.length !== originalValues.memberFeedbackSelections.length ||
      !memberFeedbackSelections.every((val: string) => originalValues.memberFeedbackSelections.includes(val)) ||
      !originalValues.memberFeedbackSelections.every((val: string) => memberFeedbackSelections.includes(val)) ||
      memberFeedbackText !== originalValues.memberFeedbackText
    )
  }, [hasExistingFeedback, originalValues, helpfulRating, memberFeedbackSelections, memberFeedbackText])

  useEffect(() => {
    if (helpfulRating && previousHelpfulRating && helpfulRating !== previousHelpfulRating) {
      setValue("memberFeedbackSelections", [])
      setValue("memberFeedbackText", "")
    }
    setPreviousHelpfulRating(helpfulRating)
  }, [helpfulRating, previousHelpfulRating, setValue])

  const getIconButtonSx = useCallback(
    (optionValue: HelpfulRating) => {
      const isSelected = helpfulRating === optionValue
      return (theme: any) => ({
        backgroundColor: isSelected ? theme.palette.text.primary : "transparent",
        color: isSelected ? theme.palette.secondary.contrastText : "inherit",
        "&:hover": {
          backgroundColor: isSelected ? theme.palette.text.primary : theme.palette?.background.hover,
          color: isSelected ? theme.palette.secondary.contrastText : "inherit"
        }
      })
    },
    [helpfulRating]
  )

  const handleClick = (optionValue: HelpfulRating) => (event: React.MouseEvent) => {
    event.preventDefault()
    setValue("helpfulRating", optionValue)
  }

  return (
    <>
      <Typography
        variant="subtitle2"
        sx={(theme) => ({
          marginBlockEnd: theme.tokens.spacing.lg,
          fontWeight: theme.typography.fontWeightMedium
        })}
      >
        <FormattedMessage defaultMessage="Was this information helpful?" />
      </Typography>

      <FormRadioGroup
        controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
        radioGroupProps={{
          row: true
        }}
        name="helpfulRating"
        options={[
          {
            value: HelpfulRating.WAS_HELPFUL,
            label: (
              <IconButton
                key={`helpful-${helpfulRating === HelpfulRating.WAS_HELPFUL}`}
                size="medium"
                aria-label={intl.formatMessage({ defaultMessage: "Yes" })}
                sx={getIconButtonSx(HelpfulRating.WAS_HELPFUL)}
                onClick={handleClick(HelpfulRating.WAS_HELPFUL)}
              >
                <ThumbsUpIcon />
              </IconButton>
            )
          },
          {
            value: HelpfulRating.WAS_NOT_HELPFUL,
            label: (
              <IconButton
                key={`not-helpful-${helpfulRating === HelpfulRating.WAS_NOT_HELPFUL}`}
                size="medium"
                aria-label={intl.formatMessage({ defaultMessage: "No" })}
                sx={getIconButtonSx(HelpfulRating.WAS_NOT_HELPFUL)}
                onClick={handleClick(HelpfulRating.WAS_NOT_HELPFUL)}
              >
                <ThumbsDownIcon />
              </IconButton>
            )
          }
        ]}
        sx={(theme) => ({
          marginBlockEnd: theme.tokens.spacing.lg,
          "& .MuiFormGroup-root": {
            gap: theme.spacing(theme.tokens.spacing.lg)
          },
          "& .MuiRadio-root": {
            display: "none"
          }
        })}
      />

      <FeedbackSelect helpfulRating={helpfulRating} />

      {helpfulRating && memberFeedbackSelections?.includes("SOMETHING_ELSE") && (
        <Box
          sx={(theme) => ({
            marginBlockStart: theme.tokens.spacing.md
          })}
        >
          <FormTextField
            name="memberFeedbackText"
            placeholder={intl.formatMessage({ defaultMessage: "Please share more about your experience." })}
            multiline
            rows={3}
            registerOptions={{
              required: intl.formatMessage({ defaultMessage: "Required" })
            }}
            sx={(theme) => ({
              marginBlockEnd: theme.tokens.spacing.xl
            })}
          />
        </Box>
      )}

      {helpfulRating && !!memberFeedbackSelections?.length && (
        <Box
          sx={(theme) => ({
            marginBlockStart: theme.tokens.spacing.md,
            display: "flex",
            justifyContent: hasExistingFeedback ? "space-between" : "flex-end",
            alignItems: "center"
          })}
        >
          {hasExistingFeedback && existingFeedbackDate && (
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage
                defaultMessage="Last edited {date}"
                values={{
                  date: intl.formatDate(existingFeedbackDate, {
                    year: "numeric",
                    month: "short",
                    day: "numeric"
                  })
                }}
              />
            </Typography>
          )}
          <FormButton variant="outlined" color="secondary" type="submit" disabled={hasExistingFeedback && !hasChanges}>
            {hasExistingFeedback ? (
              <FormattedMessage defaultMessage="Update feedback" />
            ) : (
              <FormattedMessage defaultMessage="Share feedback" />
            )}
          </FormButton>
        </Box>
      )}

      {isError && (
        <Box
          sx={(theme) => ({
            marginBlockStart: theme.tokens.spacing.sm,
            display: "flex",
            justifyContent: "flex-end"
          })}
        >
          <Typography variant="body2" color="error">
            <FormattedMessage defaultMessage="Sorry, an error has occurred. Please try again." />
          </Typography>
        </Box>
      )}
    </>
  )
}

export function FeedbackContainer({
  contentfulEntryId,
  categoryEntryId,
  contentType
}: FeedbackContainerProps): JSX.Element {
  const { mutateAsync, isError } = useCreateMemberContentFeedback()
  const { data: existingFeedback, isLoading } = useMemberContentFeedback(contentfulEntryId, categoryEntryId)

  const getDefaultValues = useCallback((): Partial<FeedbackFormValues> => {
    if (!existingFeedback) {
      return {
        helpfulRating: "",
        memberFeedbackSelections: [],
        memberFeedbackText: ""
      }
    }

    return {
      helpfulRating: existingFeedback.wasHelpful ? HelpfulRating.WAS_HELPFUL : HelpfulRating.WAS_NOT_HELPFUL,
      memberFeedbackSelections: existingFeedback.memberFeedbackSelections,
      memberFeedbackText: existingFeedback.memberFeedbackText || ""
    }
  }, [existingFeedback])

  const originalValues = React.useMemo(() => {
    if (!existingFeedback) return undefined
    return getDefaultValues() as FeedbackFormValues
  }, [existingFeedback, getDefaultValues])

  const handleFormSubmit = async (values: FeedbackFormValues) => {
    if (!values.helpfulRating || !values.memberFeedbackSelections?.length) return

    if (existingFeedback && originalValues) {
      const hasChanges =
        values.helpfulRating !== originalValues.helpfulRating ||
        values.memberFeedbackSelections.length !== originalValues.memberFeedbackSelections.length ||
        !values.memberFeedbackSelections.every((val: string) =>
          originalValues.memberFeedbackSelections.includes(val)
        ) ||
        !originalValues.memberFeedbackSelections.every((val: string) =>
          values.memberFeedbackSelections.includes(val)
        ) ||
        values.memberFeedbackText !== originalValues.memberFeedbackText

      if (!hasChanges) {
        return
      }
    }

    try {
      let memberFeedbackText: string | undefined
      if (values.memberFeedbackSelections.includes("SOMETHING_ELSE") && values.memberFeedbackText) {
        memberFeedbackText = values.memberFeedbackText
      } else {
        memberFeedbackText = undefined
      }

      const request: CreateFeedbackRequest = {
        contentfulEntryId,
        categoryEntryId,
        contentType,
        wasHelpful: values.helpfulRating === HelpfulRating.WAS_HELPFUL,
        memberFeedbackSelections: values.memberFeedbackSelections,
        memberFeedbackText
      }

      await mutateAsync(request)
    } catch (error) {
      reportError(error)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Progress />
      </Box>
    )
  }

  return (
    <Box
      sx={(theme) => ({
        marginInlineEnd: { xs: 0, sm: theme.tokens.spacing.xxxl },
        marginBlockEnd: theme.tokens.spacing.lg,
        maxInlineSize: { xs: "100%", sm: "28rem" }
      })}
    >
      <Form<FeedbackFormValues> onSubmit={handleFormSubmit} defaultValues={getDefaultValues()}>
        <FeedbackFormContent
          hasExistingFeedback={!!existingFeedback}
          existingFeedbackDate={existingFeedback?.createdAtUtc}
          originalValues={originalValues}
          isError={isError}
        />
      </Form>
    </Box>
  )
}
